import React, { useEffect, useState } from "react";
import {
  Container,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Fab,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add, MoreVert } from "@material-ui/icons";
import { useIntl } from "react-intl";

import FloatingActions from "../../ui/FloatingActions";
import Confirm from "../../ui/Confirm";

import { useBmapi } from "../../utils/bmapi-context";
import { getErrorMessageString } from "../../utils/errors";
import { account, common } from "../../messages";
import GenListDropdown from "../../components/genlist/GenListDropdown";

import { useQuestionsCache } from "./ManageQuiz"; // cache condivisa tra tab

const useStyles = makeStyles(() => ({
  primaryClamp2: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  fabWrap: { position: "relative", display: "inline-flex" },
  fabProgress: {
    position: "absolute",
    top: -6,
    right: -6,
    zIndex: 1,
  },
  filterRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    padding: "8px 0 16px 0",
  },
  filterDropdown: {
    flex: "0 0 240px",
  },
  filterSearch: {
    flex: "1 1 auto",
  },
}));

// normalizza per sort: ignora punteggiatura iniziale, case-insensitive
function normalizeForMatch(item) {
  const raw = (item?.text ?? item?.name ?? "").toString().trimStart() || "";
  const cleaned = raw.replace(/^[^\p{L}\p{N}]+/u, ""); // leva ', ", ., ecc. all'inizio
  return cleaned.toLowerCase();
}

// normalizza per filtro LIKE: include anche description
function normalizeForSearch(item) {
  const base =
    (item?.text ?? item?.name ?? "") + " " + (item?.description ?? "");
  const cleaned = base.replace(/^[^\p{L}\p{N}]+/u, "");
  return cleaned.toLowerCase();
}

/**
 * ManageQuizQuestions
 *
 * - Usa la cache globale delle domande (questions) dal provider di ManageQuiz
 * - Se la cache è vuota, la popola con loadQuestionsOnce()
 * - Filtra per categoria + testo
 * - Gestisce create/edit/delete con update ottimistico sulla cache
 */
export default function ManageQuizQuestions() {
  const classes = useStyles();
  const intl = useIntl();

  // dal contesto globale Bmapi, prendiamo solo quello che usiamo
  const { bmapi, notifyError, notifySuccess } = useBmapi();

  // cache condivisa tra tab
  const {
    questions,
    setQuestions,
    loadQuestionsOnce,
    loadingQuestions,
  } = useQuestionsCache();

  const [selectedId, setSelectedId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // conferme
  const [deleteRequest, setDeleteRequest] = useState(false);
  const [closeEditorConfirm, setCloseEditorConfirm] = useState(false);

  // filtro categoria (singolo id, multiple={false})
  const [filter, setFilter] = useState({
    category: "",
  });

  // ricerca testuale libera
  const [searchText, setSearchText] = useState("");

  // stato editor domanda
  const [editor, setEditor] = useState({
    open: false,
    mode: "create", // "create" | "edit"
    id: null,
    values: {
      text: "",
      explanation: "",
    },
    errors: { text: false },
    dirty: false,
    busy: false,
  });

  // carica la cache al primo render / quando entri nel tab
  useEffect(() => {
    loadQuestionsOnce();
  }, [loadQuestionsOnce]);

  // --- MENU context actions ---
  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };
  const handleMenuClose = () => setAnchorEl(null);

  // --- EDITOR open/close ---
  const openCreateEditor = () =>
    setEditor({
      open: true,
      mode: "create",
      id: null,
      values: { text: "", explanation: "" },
      errors: { text: false },
      dirty: false,
      busy: false,
    });

  const openEditEditor = (id) => {
    const item = questions.find((x) => x.id === id);
    setEditor({
      open: true,
      mode: "edit",
      id,
      values: {
        text: item?.name ?? item?.text ?? "",
        explanation: item?.description ?? "",
      },
      errors: { text: false },
      dirty: false,
      busy: false,
    });
  };

  const requestCloseEditor = () => {
    if (editor.busy) return;
    if (editor.dirty) {
      setCloseEditorConfirm(true);
      return;
    }
    setEditor((s) => ({ ...s, open: false }));
  };

  const confirmCloseEditor = () => {
    setCloseEditorConfirm(false);
    setEditor((s) => ({ ...s, open: false, dirty: false }));
  };

  // --- HANDLER CAMPI ---
  const handleFilter = (field) => (e) => {
    const val = e.target.value;
    setFilter((s) => ({
      ...s,
      [field]: val,
    }));
  };

  const handleSearchText = (e) => {
    setSearchText(e.target.value || "");
  };

  const handleField = (field) => (e) => {
    const val = e.target.value;
    setEditor((s) => ({
      ...s,
      values: { ...s.values, [field]: val },
      dirty: true,
      errors: { ...s.errors, [field]: false },
    }));
  };

  // --- VALIDAZIONE EDITOR ---
  const validate = () => {
    const textInvalid = !editor.values.text?.trim();
    const errs = { text: textInvalid };
    setEditor((s) => ({ ...s, errors: errs }));
    return !textInvalid;
  };

  // --- SALVA (CREATE / UPDATE) ---
  const saveEditor = async () => {
    if (!bmapi) return;
    if (!validate()) return;

    setEditor((s) => ({ ...s, busy: true }));
    try {
      const payload = {
        name: editor.values.text.trim(),
        description: editor.values.explanation?.trim() ?? "",
      };

      if (editor.mode === "edit" && editor.id) {
        // UPDATE
        await bmapi.updateCourse(editor.id, payload);

        // aggiorna ottimisticamente in cache
        setQuestions((curr) =>
          curr.map((q) =>
            q.id === editor.id
              ? {
                  ...q,
                  name: payload.name,
                  text: payload.name, // se backend usa .text, lo teniamo sincronizzato
                  description: payload.description,
                }
              : q
          )
        );
      } else {
        // CREATE
        const created = await bmapi.createCourse(payload);

        // inserisci nella cache:
        // se l'API ritorna l'oggetto nuovo lo usiamo,
        // altrimenti fallback con id temporaneo
        setQuestions((curr) => [
          ...curr,
          created || {
            id: Math.random().toString(36).slice(2),
            name: payload.name,
            text: payload.name,
            description: payload.description,
          },
        ]);
      }

      notifySuccess(intl.formatMessage(account.saveConfirm));
      setEditor((s) => ({ ...s, busy: false, open: false, dirty: false }));
    } catch (e) {
      setEditor((s) => ({ ...s, busy: false }));
      notifyError(getErrorMessageString(e, intl));
    }
  };

  // --- DELETE ---
  const handleDeleteClick = () => {
    setDeleteRequest(true);
    handleMenuClose();
  };

  const onDelete = async () => {
    if (!selectedId) return;
    try {
      await bmapi.deleteCourse(selectedId);

      // rimuovi ottimisticamente la question dalla cache
      setQuestions((curr) => curr.filter((q) => q.id !== selectedId));

      notifySuccess(intl.formatMessage(common.deleteElement));
      setDeleteRequest(false);
      setSelectedId(null);
    } catch (e) {
      notifyError(getErrorMessageString(e, intl));
    }
  };

  // --- DERIVATI PER LA VIEW ---

  // per il dropdown categoria vogliamo una stringa stabile
  const categoryValue = filter.category ?? "";

  // testo libero normalizzato
  const needle = searchText.trim().toLowerCase();
  const selectedCategory = categoryValue || null;

  // Filtro e sort locale delle domande:
  // 1. filtro LIKE su testo+descrizione
  // 2. filtro categoria (assumo question.categories o question.category)
  // 3. sort alfabetico ignorando punteggiatura iniziale
  const visibleList = questions
    .filter((item) => {
      // filtro testo
      if (needle) {
        if (!normalizeForSearch(item).includes(needle)) return false;
      }
      // filtro categoria se selezionata
      if (selectedCategory) {
        const cats = item.categories || item.category || [];
        const catArr = Array.isArray(cats) ? cats : [cats];
        if (!catArr.includes(selectedCategory)) return false;
      }
      return true;
    })
    .slice()
    .sort((a, b) =>
      normalizeForMatch(a).localeCompare(normalizeForMatch(b), undefined, {
        sensitivity: "base",
      })
    );

  // se sto ancora caricando la cache la prima volta e non ho dati -> spinner
  const isLoadingInitial = loadingQuestions && questions.length === 0;

  return (
    <Container disableGutters>
      {/* filtro categoria + ricerca testo */}
      <Box className={classes.filterRow}>
        <Box className={classes.filterDropdown}>
          <GenListDropdown
            onChange={handleFilter("category")}
            margin="dense"
            label="Categoria"
            listId="quiz-categories"
            value={categoryValue}
            name="category"
            multiple={false}
            lang="it"
            sortByLabel={true}
          />
        </Box>

        <Box className={classes.filterSearch}>
          <TextField
            label="Cerca testo"
            variant="standard"
            margin="dense"
            fullWidth
            value={searchText}
            onChange={handleSearchText}
          />
        </Box>
      </Box>

      {isLoadingInitial ? (
        <Box p={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {visibleList.map((item, idx) => (
            <React.Fragment key={item.id}>
              {idx > 0 && <Divider component="li" />}
              <ListItem>
                <ListItemText
                  primary={item.text ?? item.name ?? ""}
                  primaryTypographyProps={{
                    className: classes.primaryClamp2,
                  }}
                  secondary={item.description}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="menu"
                    onClick={(e) => handleMenuOpen(e, item.id)}
                  >
                    <MoreVert />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      {/* menu azioni per singola domanda */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            openEditEditor(selectedId);
            handleMenuClose();
          }}
        >
          Modifica
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>Elimina</MenuItem>
      </Menu>

      {/* popup editor domanda */}
      <Dialog
        open={editor.open}
        onClose={requestCloseEditor}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editor.mode === "edit" ? "Modifica domanda" : "Nuova domanda"}
        </DialogTitle>

        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Testo domanda"
            value={editor.values.text}
            onChange={handleField("text")}
            fullWidth
            variant="standard"
            error={editor.errors.text}
            helperText={editor.errors.text ? "Obbligatorio" : ""}
            disabled={editor.busy}
          />

          <TextField
            margin="dense"
            label="Spiegazione (opzionale)"
            value={editor.values.explanation}
            onChange={handleField("explanation")}
            fullWidth
            variant="standard"
            multiline
            rows={3}
            disabled={editor.busy}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={requestCloseEditor} disabled={editor.busy}>
            Chiudi
          </Button>
          <Box position="relative" display="inline-flex">
            <Button color="primary" onClick={saveEditor} disabled={editor.busy}>
              Salva
            </Button>
            {editor.busy && (
              <CircularProgress
                size={32}
                style={{ position: "absolute", top: -6, right: -10, zIndex: 1 }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* conferme */}
      <Confirm
        open={!!closeEditorConfirm}
        onConfirm={confirmCloseEditor}
        onCancel={() => setCloseEditorConfirm(false)}
        text="Hai modifiche non salvate. Uscire comunque?"
      />
      <Confirm
        open={!!deleteRequest}
        onConfirm={onDelete}
        onCancel={() => setDeleteRequest(false)}
        text="Confermi l'eliminazione?"
      />

      {/* azione floating */}
      <FloatingActions>
        <span className={classes.fabWrap}>
          <Fab
            variant="extended"
            color="primary"
            onClick={openCreateEditor}
            disabled={editor.busy}
          >
            <Add />
            Nuova domanda
          </Fab>
          {editor.busy && (
            <CircularProgress size={48} className={classes.fabProgress} />
          )}
        </span>
      </FloatingActions>
    </Container>
  );
}
